import classnames from 'classnames';
import Image from 'next/image';
import type { FC, ReactNode } from 'react';
import React, { useMemo } from 'react';

import { Loader } from '../../components/Loader';
import { apiUrl } from '../../config';
import { useWebsite } from '../../hooks/useWebsite';
import { ReactComponent as IconArrowPrev } from '../../icons/icon-arrow-previous.svg';
import { ReactComponent as LogoGraphComment } from '../../icons/logo-graphcomment.svg';
import { computeImageSource } from '../../utils/string';

export const WebsiteLogo: React.FC<{ className?: string }> = ({ className }) => {
  const { custom, loading } = useWebsite();
  const imageSrc = computeImageSource(custom?.image);

  if (loading) return null;

  // Custom logo uploaded by a premium client → keep as <img> (their PNG/JPG).
  // Dark mode readability for those falls back to the dark:drop-shadow halo
  // applied at the call site (WebsiteLayout.tsx).
  if (imageSrc && imageSrc.length > 0) {
    return (
      <Image
        alt="logo"
        className={classnames('h-18 object-contain', className)}
        src={imageSrc}
        width={500}
        height={120}
      />
    );
  }

  // Default Semiologic logo: inline SVG so currentColor (set via the parent
  // `text-*` class) drives the wordmark and tagline color. The G picto keeps
  // its coral gradient, the text turns light in dark mode.
  return (
    <LogoGraphComment
      className={classnames('h-[3.35rem] w-auto text-black dark:text-white', className)}
      role="img"
      aria-label="GraphComment"
    />
  );
};

interface Props {
  className?: string;
  withBackIcon?: boolean;
  // Force le logo affiché, en ignorant `logo.displayKind` du tenant.
  //   - undefined (défaut, MENU) : suit le CHOIX EXPLICITE du tenant (`displayKind`) ; si le
  //     champ est ABSENT (tenant non migré), retombe sur l'ancien comportement ('legacy') →
  //     le logo historique (logo.image) reste servi tel quel, ZÉRO régression sans migration.
  //   - 'legacy' (POPINS AdultConsent/ProfileCreation) : reproduit à l'identique l'ancien
  //     comportement (image = le logo uploadé, affichée seulement si `imageActive`).
  forceKind?: 'rect' | 'square' | 'legacy';
}

const RECT_FALLBACK = 'https://login.graphcomment.com/img/logo-graphcomment.png';

export const WebsiteCustomLogo: FC<Props> = props => {
  const { className, withBackIcon, forceKind } = props;
  const { name, custom: customConfig, loading } = useWebsite();
  let { logo } = useMemo(() => customConfig ?? {}, [customConfig]);

  // Choix effectif du logo.
  //   - forceKind fourni (popins) → il prime.
  //   - sinon, le menu suit le CHOIX EXPLICITE `logo.displayKind` s'il vaut 'rect' ou 'square'
  //     (posé par la nouvelle UI ou une migration).
  //   - sinon (champ ABSENT = tenant non migré) → 'legacy' : rétrocompat stricte, on n'impose
  //     PAS 'rect' aux ~centaines de tenants existants qui affichent leur logo historique via
  //     `logo.image` + `imageActive` (sinon bascule visuelle non voulue sur Psychologies,
  //     Beauté-Test, NextCircle… incident évité 2026-07-18).
  const kind: 'rect' | 'square' | 'legacy' =
    forceKind ??
    (logo?.displayKind === 'square' ? 'square' : logo?.displayKind === 'rect' ? 'rect' : 'legacy');

  const squareId = logo?.image?.id;
  const squareUrl = squareId ? `${apiUrl}/api/file/${squareId}` : null;
  const rectSrcRaw = computeImageSource(customConfig?.image);
  const rectUrl = rectSrcRaw && rectSrcRaw.length > 0 ? rectSrcRaw : null;

  // Résolution de l'image + de sa visibilité selon le choix :
  //   - legacy : ancien comportement exact (tenant non migré) — image = carré, visible SEULEMENT
  //     si imageActive. INTOUCHÉ (zéro régression sur les tenants prod existants).
  //   - rect / square : choix EXPLICITE (nouvelle UI). L'image ne s'affiche que si « Afficher le
  //     logo » est coché (`imageActive`) → permet « texte seul » (imageActive=false). Le
  //     displayKind ne décide QUE lequel des 2 logos (rect=custom.image, square=logo.image),
  //     pas s'il s'affiche. Le texte est géré séparément plus bas (`textActive`).
  let src: string | undefined | null;
  let showImage: boolean;
  if (kind === 'legacy') {
    showImage = !!logo?.imageActive;
    src =
      logo?.image && logo?.imageActive ? squareUrl : rectUrl ?? (loading ? null : RECT_FALLBACK);
  } else if (kind === 'square') {
    showImage = !!logo?.imageActive;
    src = squareUrl ?? rectUrl ?? (loading ? null : RECT_FALLBACK);
  } else {
    // rect
    showImage = !!logo?.imageActive;
    src = rectUrl ?? squareUrl ?? (loading ? null : RECT_FALLBACK);
  }

  // The width used to be: `w-[min(220px,100%)]`
  const widthClass = 'w-auto';
  const heightClass = logo?.textActive ? 'h-8' : 'h-10';

  let loaderElt: ReactNode | undefined = undefined;
  if (src === null && loading) {
    loaderElt = (
      <div className={`flex items-center justify-center ${widthClass} ${heightClass}`}>
        <Loader black />
      </div>
    );
  }

  const img = showImage && (
    <div className="flex-1 relative flex items-center">
      {loaderElt || (
        <Image
          className={classnames('object-contain object-left', widthClass, heightClass, className)}
          src={src ?? ''}
          alt={name ?? ''}
          width={220}
          height={36}
          // priority
        />
      )}
    </div>
  );

  const txt = logo?.textActive && <span className="_sm:text-xs truncate">{logo.text}</span>;

  const imgTxtBlock = txt ? (
    <div className="flex-1 flex flex-col">
      {img}
      {txt}
    </div>
  ) : (
    img
  );

  return withBackIcon ? (
    <div className="flex-1 flex flex-row space-x-2">
      <IconArrowPrev className="w-2 h-3 self-center text-primary" />
      {imgTxtBlock}
    </div>
  ) : (
    imgTxtBlock || null
  );
};
